import { ORDER_STATUS_OPEN_STATE } from '@shared/constants/orderStatusOpenState';
import type { PickerSearchCollectionKey, SearchCollectionKey } from '@shared/constants/searchCollections';
import { type OrderSearchType, TYPESENSE_ORDER_TYPE } from '@shared/core/search/entities/order';

/**
 * Section registry for the ⌘K palette (TAS-3542) — the axis that drives search
 * legs, facet chips, result groups and scope. One entry == one SECTION.
 *
 *   collections.ts  ──▶ HOW a doc renders   (per COLLECTION; reads the discriminator)
 *   this module     ──▶ WHICH slice to show (collection + optional discriminator + label)
 *
 * A section is `{ collection, optional discriminator }`. Collections whose docs
 * fold several sub-types under one faceted field split into several sections:
 *
 *   orders  ─(orderType)─▶  Sales · Purchase · Grower Product · Buy-Sell   (4)
 *   bills   ─(billType)──▶  Invoices · Payables                            (2)
 *   every other collection                                    stays 1:1   (9)
 *                                                                  ───────────
 *                                                          15 facetable sections
 *
 * Each leg carries its discriminator as a filter clause in the engine's own field
 * (Typesense `orderType` / `billType`, Algolia `__type` for orders), so every type
 * gets a guaranteed slot instead of relevance handing one federated `orders` leg
 * 20 sales orders and zero POs.
 *
 * `order:` / `bill:` remain as UMBRELLA sections (whole collection, no
 * discriminator). They are reachable ONLY by their token — never rendered as a
 * facet chip and never a leg in the all view — so the chip row stays the 15.
 */

/** The collections the palette renders: every search collection plus the picker-granted business entities. */
export type PaletteCollectionKey = SearchCollectionKey | PickerSearchCollectionKey;

/** The collections Algolia also indexes; on an Algolia org the palette searches the entitled ones among these. */
export const ALGOLIA_PALETTE_COLLECTIONS: readonly PaletteCollectionKey[] = [
  'orders',
  'products',
  'lots',
  'productionRuns',
  'businessEntities',
];

/** Narrows a collection to a sub-type via a faceted field; an order section also pins `orderType`, which Algolia matches as `__type`. */
export interface SectionDiscriminator {
  /** Faceted Typesense field: `orderType` for orders, `billType` for bills. */
  field: string;
  /** Discriminator value the section pins, e.g. `SALES_ORDER` / `INVOICE`. */
  value: string;
}

export interface SearchSection {
  /** Stable section id — scope identity, result-group key, React key. */
  key: string;
  /** Underlying collection (the Typesense collection or Algolia index of that name); drives entitlement, the queried fields and rendering. */
  collection: PaletteCollectionKey;
  /** Singular section label; the UI pluralises it for chips + group headers. */
  label: string;
  /** Optional discriminator narrowing the collection to this section. */
  discriminator?: SectionDiscriminator;
  /** The order type an order section pins; Algolia stores it as `__type`, Typesense as `orderType`. */
  orderType?: OrderSearchType;
  /** Open/closed status sets for the `is:open` / `is:closed` filter (order sections only). */
  statusOpenState?: { open: string[]; closed: string[] };
}

const orderSection = (key: string, orderType: OrderSearchType, label: string): SearchSection => ({
  key,
  collection: 'orders',
  label,
  discriminator: { field: 'orderType', value: TYPESENSE_ORDER_TYPE[orderType] },
  orderType,
  statusOpenState: ORDER_STATUS_OPEN_STATE[TYPESENSE_ORDER_TYPE[orderType]],
});

/**
 * The facetable sections, in fixed render order. The four order sections stay
 * adjacent (SO · PO · GP · BSO), and each collection's sections sit where that
 * collection sat in the federated order.
 */
export const SECTIONS: readonly SearchSection[] = [
  orderSection('sales-orders', 'SalesOrder', 'Sales Order'),
  orderSection('purchase-orders', 'PurchaseOrder', 'Purchase Order'),
  orderSection('grower-products', 'WorkOrder', 'Grower Product'),
  orderSection('buy-sell', 'BuySellOrder', 'Buy-Sell Order'),
  { key: 'products', collection: 'products', label: 'Product' },
  { key: 'lots', collection: 'lots', label: 'Lot' },
  { key: 'business-entities', collection: 'businessEntities', label: 'Business Entity' },
  { key: 'production-runs', collection: 'productionRuns', label: 'Production Run' },
  { key: 'invoices', collection: 'bills', label: 'Invoice', discriminator: { field: 'billType', value: 'INVOICE' } },
  { key: 'payables', collection: 'bills', label: 'Payable', discriminator: { field: 'billType', value: 'PAYABLE' } },
  { key: 'payments', collection: 'payments', label: 'Payment' },
  { key: 'inventory-transfers', collection: 'inventoryTransfers', label: 'Inventory Transfer' },
  { key: 'receiving-tickets', collection: 'receivingTickets', label: 'Receiving Ticket' },
  { key: 'pick-tickets', collection: 'pickTickets', label: 'Pick Ticket' },
  { key: 'ledger-accounts', collection: 'ledgerAccounts', label: 'Account' },
];

/** Umbrella scopes (`order:` / `bill:`): the whole collection, no discriminator. Token-only. */
export const UMBRELLA_SECTIONS: readonly SearchSection[] = [
  { key: 'orders', collection: 'orders', label: 'Order' },
  { key: 'bills', collection: 'bills', label: 'Bill' },
];

const SECTION_BY_KEY = new Map<string, SearchSection>(
  [...SECTIONS, ...UMBRELLA_SECTIONS].map((section) => [section.key, section])
);

/** Look up a section (facetable or umbrella) by its key. */
export function getSectionConfig(key: string): SearchSection | undefined {
  return SECTION_BY_KEY.get(key);
}

/** The facetable sections whose collection the caller is entitled to, in registry order. */
export function getEntitledSections(collections: readonly string[]): SearchSection[] {
  const entitled = new Set(collections);
  return SECTIONS.filter((section) => entitled.has(section.collection));
}

/**
 * A stub document carrying only the section's discriminator, for the per-collection
 * render functions (`accentKey`, `typeLabel`) to resolve a section's identity before
 * any hit lands — e.g. the PO scope pill's purple accent from an empty result set.
 */
export function sectionDiscriminatorDoc(section: SearchSection): Record<string, string> {
  return section.discriminator ? { [section.discriminator.field]: section.discriminator.value } : {};
}
